if (!customElements.get('product-item-media-slider')) {
	class ProductItemMediaSlider extends HTMLElement {
		constructor() {
			super();
			this._observer = null;
			this.mode = this.getAttribute('nav') || 'dots';
		}

		connectedCallback() {
			if ('IntersectionObserver' in window) {
				this._observer = new IntersectionObserver(
					(entries, observer) => {
						if (!entries[0].isIntersecting) return;

						observer.unobserve(this);
						this.init();
					},
					{ rootMargin: '0px 0px 50px 0px' }
				);

				this._observer.observe(this);
			} else {
				this.init();
			}
		}

		disconnectedCallback() {
			this.removeListeners();

			if (this._observer) {
				this._observer.disconnect();
				this._observer = null;
			}
		}

		init() {
			this.slides = this.querySelectorAll('.media-slider__item');

			if (this.slides.length < 2) return;

			this.slider = this.querySelector('.media-slider__slides');
			this.grid = this.querySelector('.media-slider__grid');
			this.nav = this.querySelector('.media-slider__nav');
			this.rtl = document.dir === 'rtl';

			if (this.nav) {
				if (this.mode === 'dots') {
					this.dotBtns = this.nav.querySelectorAll('.media-slider__nav-btn');
				}

				if (this.mode === 'arrows') {
					this.prevBtn = this.nav.querySelector('button[name="prev"]');
					this.nextBtn = this.nav.querySelector('button[name="next"]');
				}
			}

			this.sliderStart = this.getWindowOffset(this.slider);
			this.sliderEnd = this.sliderStart + this.slider.clientWidth;

			const w = this.slides[0].clientWidth || 1;
			this.currentIndex = Math.round(this.slider.scrollLeft / w) || 0;

			this.addListeners();
			this.setButtonStates();
		}

		addListeners() {
			if (!this.nav) return;

			this.scrollHandler = this.debounce(this.handleScroll.bind(this));
			this.slider.addEventListener('scroll', this.scrollHandler);

			if (this.mode === 'dots') {
				this.dotClickHandler = this.handleDotClick.bind(this);
				this.nav.addEventListener('click', this.dotClickHandler);
			}

			if (this.mode === 'arrows') {
				this.navClickHandler = this.handleNavClick.bind(this);
				this.nav.addEventListener('click', this.navClickHandler);
			}
		}

		removeListeners() {
			if (!this.nav) return;

			this.slider.removeEventListener('scroll', this.scrollHandler);

			if (this.mode === 'dots') {
				this.nav.removeEventListener('click', this.dotClickHandler);
			}

			if (this.mode === 'arrows') {
				this.nav.removeEventListener('click', this.navClickHandler);
			}
		}

		debounce(func, wait = 100) {
			let timer;
			return function (...args) {
				clearTimeout(timer);
				timer = setTimeout(() => func.apply(this, args), wait);
			};
		}

		handleScroll() {
			this.sliderStart = this.getWindowOffset(this.slider);
			this.sliderEnd = this.sliderStart + this.slider.clientWidth;
			this.currentIndex = Math.round(this.slider.scrollLeft / this.slides[0].clientWidth);

			this.setButtonStates();
		}

		handleNavClick(evt) {
			if (!evt.target.matches('.media-slider__nav-btn')) return;

			if (evt.target.name === 'next' && !this.rtl) {
				this.scrollPos = this.slider.scrollLeft + this.slides[0].clientWidth;
			} else {
				this.scrollPos = this.slider.scrollLeft - this.slides[0].clientWidth;
			}

			this.slider.scrollTo({ left: this.scrollPos, behavior: 'smooth' });
		}

		handleDotClick(evt) {
			if (!evt.target.matches('.media-slider__nav-btn')) return;

			const index = Array.from(this.dotBtns).indexOf(evt.target);
			if (index === -1) return;

			this.scrollPos = this.slides[0].clientWidth * index;
			this.slider.scrollTo({ left: this.scrollPos, behavior: 'smooth' });
		}

		getWindowOffset(el) {
			return this.rtl ? window.innerWidth - el.getBoundingClientRect().right : el.getBoundingClientRect().left;
		}

		setButtonStates() {
			if (this.mode === 'dots' && this.dotBtns) {
				this.dotBtns.forEach((btn, i) => {
					btn.classList.toggle('is-active', i === this.currentIndex);
				});
			}

			if (this.mode === 'arrows') {
				if (!this.prevBtn && !this.nextBtn) return;

				this.prevBtn.disabled = this.slider.scrollLeft <= 0;
				const maxScrollLeft = this.slider.scrollWidth - this.slider.clientWidth;
				this.nextBtn.disabled = this.slider.scrollLeft >= maxScrollLeft;
			}
		}
	}

	customElements.define('product-item-media-slider', ProductItemMediaSlider);
}
